import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./MockExamResults.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, QBankCode } from "../utils/qbankProfile";
import QBankQuestionImage, { QBankImage } from "../components/QBankQuestionImage";

interface Option {
    label: string;
    text: string;
}

interface Question {
    id: string;
    collection_id?: string | null;
    collection?: {
        id: string;
        title: string;
        year: number;
        month: number;
    } | null;
    question_text: string;
    options: Option[];
    specialty: string;
    correct_answer: string;
    explanation: string | null;
    question_references: any[] | null;
    user_answer: string | null;
    is_correct: boolean;
    flagged: boolean;
    question_image?: QBankImage | null;
}

interface SpecialtyBreakdown {
    specialty: string;
    total: number;
    correct: number;
}

interface ExamResults {
    exam: {
        id: string;
        status: string;
        started_at: string;
        completed_at: string;
        time_taken_seconds: number;
        total_questions: number;
        correct_count: number;
    };
    questions: Question[];
    specialty_breakdown: SpecialtyBreakdown[];
    score: {
        correct: number;
        total: number;
        percentage: number;
    };
    time_taken_seconds: number;
}

const MockExamResults: React.FC = () => {
    const { bank: bankParam, examId } = useParams<{ bank: string; examId: string }>();
    const bank: QBankCode = isQBankCode(bankParam) ? bankParam : "sdle";
    const { user } = useAuthContext();
    const navigate = useNavigate();

    const [results, setResults] = useState<ExamResults | null>(null);
    const [loading, setLoading] = useState(true);
    const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
    const [filterMode, setFilterMode] = useState<"all" | "flagged">("all");

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    useEffect(() => {
        if (user?.id && examId) {
            loadResults();
        }
    }, [user, examId, bank]);

    const loadResults = async () => {
        if (!user?.id || !examId) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/mock-exams/${examId}/results?user_id=${user.id}&bank=${bank}`
            );
            if (res.ok) {
                const data = await res.json();
                setResults(data);
            } else {
                navigate(`/qbank/${bank}/mock-exams`);
            }
        } catch (err) {
            console.error("Failed to load results", err);
            navigate(`/qbank/${bank}/mock-exams`);
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (seconds: number) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}h ${mins}m ${secs}s`;
        }
        return `${mins}m ${secs}s`;
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getScoreClass = (percent: number) => {
        if (percent >= 70) return "good";
        if (percent >= 50) return "medium";
        return "low";
    };

    const toggleQuestion = (questionId: string) => {
        setExpandedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(questionId)) {
                next.delete(questionId);
            } else {
                next.add(questionId);
            }
            return next;
        });
    };

    if (loading) {
        return (
            <div className={styles.pageContainer}>
                <Header />
                <main className={styles.mainContent}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner} />
                        <p>Loading results...</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!results) {
        return null;
    }

    const { exam, questions, specialty_breakdown, score } = results;
    const scoreClass = getScoreClass(score.percentage);

    // Filter and count flagged questions
    const flaggedCount = questions.filter((q) => q.flagged).length;
    const displayedQuestions = filterMode === "flagged"
        ? questions.filter((q) => q.flagged)
        : questions;

    return (
        <div className={styles.pageContainer}>
            <SEO
                title="Exam Results | Mock Exams | C-Shaped"
                description="Review your mock exam results and performance breakdown."
            />
            <Header />

            <main className={styles.mainContent}>
                {/* Header */}
                <div className={styles.resultsHeader}>
                    <h1 className={styles.resultsTitle}>Exam Results</h1>
                    <p className={styles.resultsSubtitle}>
                        Completed on {formatDate(exam.completed_at)}
                    </p>
                </div>

                {/* Score Card */}
                <div className={styles.scoreCard}>
                    <div className={styles.scoreMain}>{score.percentage}%</div>
                    <div className={styles.scoreLabel}>
                        {score.correct} out of {score.total} correct
                    </div>
                    <div className={styles.scoreStats}>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>
                                {formatDuration(exam.time_taken_seconds)}
                            </span>
                            <span className={styles.statLabel}>Time Taken</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>
                                {questions.filter((q) => !q.user_answer).length}
                            </span>
                            <span className={styles.statLabel}>Skipped</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>
                                {specialty_breakdown.length}
                            </span>
                            <span className={styles.statLabel}>Specialties</span>
                        </div>
                    </div>
                </div>

                {/* Specialty Breakdown */}
                <section className={styles.breakdownSection}>
                    <h2 className={styles.sectionTitle}>Performance by Specialty</h2>
                    <div className={styles.specialtyGrid}>
                        {specialty_breakdown.map((spec) => {
                            const percent =
                                spec.total > 0
                                    ? Math.round((spec.correct / spec.total) * 100)
                                    : 0;
                            const specClass = getScoreClass(percent);
                            return (
                                <div key={spec.specialty} className={styles.specialtyCard}>
                                    <div className={styles.specialtyName}>
                                        {spec.specialty}
                                    </div>
                                    <div className={styles.specialtyScore}>
                                        <div className={styles.specialtyBar}>
                                            <div
                                                className={`${styles.specialtyFill} ${styles[specClass]
                                                    }`}
                                                style={{ width: `${percent}%` }}
                                            />
                                        </div>
                                        <span
                                            className={`${styles.specialtyPercent} ${styles[specClass]
                                                }`}
                                        >
                                            {spec.correct}/{spec.total}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Question Review */}
                <section className={styles.questionsSection}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Question Review</h2>
                        <div className={styles.filterTabs}>
                            <button
                                className={`${styles.filterTab} ${filterMode === "all" ? styles.filterTabActive : ""}`}
                                onClick={() => setFilterMode("all")}
                            >
                                All Questions ({questions.length})
                            </button>
                            <button
                                className={`${styles.filterTab} ${filterMode === "flagged" ? styles.filterTabActive : ""}`}
                                onClick={() => setFilterMode("flagged")}
                                disabled={flaggedCount === 0}
                            >
                                🚩 Flagged Only ({flaggedCount})
                            </button>
                        </div>
                    </div>

                    {displayedQuestions.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No flagged questions to review.</p>
                            <button
                                className={styles.showAllButton}
                                onClick={() => setFilterMode("all")}
                            >
                                Show All Questions
                            </button>
                        </div>
                    ) : (
                        displayedQuestions.map((q) => {
                            // Find original index for question number display
                            const originalIndex = questions.findIndex((orig) => orig.id === q.id);
                            const isExpanded = expandedQuestions.has(q.id);
                            const statusClass = !q.user_answer
                                ? "skipped"
                                : q.is_correct
                                    ? "correct"
                                    : "incorrect";

                            return (
                                <div
                                    key={q.id}
                                    className={`${styles.questionCard} ${styles[statusClass]}`}
                                >
                                    <div className={styles.questionMeta}>
                                        <div className={styles.questionMetaLeft}>
                                            <span className={styles.questionNum}>
                                                Question {originalIndex + 1}
                                                {q.flagged && (
                                                    <span className={styles.flaggedBadge}>🚩</span>
                                                )}
                                            </span>
                                            {q.collection?.title && (
                                                <span className={styles.collectionBadge}>
                                                    {q.collection.title}
                                                </span>
                                            )}
                                        </div>
                                        <span
                                            className={`${styles.resultBadge} ${statusClass === "correct"
                                                ? styles.badgeCorrect
                                                : statusClass === "incorrect"
                                                    ? styles.badgeIncorrect
                                                    : styles.badgeSkipped
                                                }`}
                                        >
                                            {statusClass === "correct"
                                                ? "✓ Correct"
                                                : statusClass === "incorrect"
                                                    ? "✗ Incorrect"
                                                    : "— Skipped"}
                                        </span>
                                    </div>

                                    <p className={styles.questionText}>{q.question_text}</p>
                                    <QBankQuestionImage image={q.question_image} compact />

                                    <div className={styles.answerSection}>
                                        <div className={styles.answerItem}>
                                            <span className={styles.answerLabel}>
                                                Your answer:
                                            </span>
                                            <span
                                                className={`${styles.answerValue} ${q.is_correct
                                                    ? styles.correct
                                                    : styles.incorrect
                                                    }`}
                                            >
                                                {q.user_answer || "—"}
                                            </span>
                                        </div>
                                        <div className={styles.answerItem}>
                                            <span className={styles.answerLabel}>
                                                Correct answer:
                                            </span>
                                            <span
                                                className={`${styles.answerValue} ${styles.correct}`}
                                            >
                                                {q.correct_answer}
                                            </span>
                                        </div>
                                    </div>

                                    {(q.explanation || (q.question_references && q.question_references.length > 0)) && (
                                        <>
                                            <button
                                                className={styles.explanationToggle}
                                                onClick={() => toggleQuestion(q.id)}
                                            >
                                                {isExpanded
                                                    ? "Hide Explanation"
                                                    : "Show Explanation"}
                                            </button>
                                            {isExpanded && (
                                                <div className={styles.explanationSection}>
                                                    {q.explanation && (
                                                        <div className={styles.explanation}>
                                                            {q.explanation}
                                                        </div>
                                                    )}
                                                    {q.question_references && q.question_references.length > 0 && (
                                                        <div className={styles.referencesSection}>
                                                            <h4 className={styles.referencesTitle}>References</h4>
                                                            {q.question_references.map((ref: any, idx: number) => {
                                                                // Get source name - try multiple fields
                                                                let sourceName = ref.source || ref.textbook || ref.source_name || "Source";
                                                                if (typeof sourceName === 'string') {
                                                                    sourceName = sourceName.replace(/\.pdf$/i, "");
                                                                }
                                                                // Get page number - try multiple fields (same pattern as QBankCollection)
                                                                const page = ref.page_number || ref.details?.page_number;
                                                                // Get excerpt - try multiple fields
                                                                const excerpt = ref.excerpt || ref.content || ref.text_excerpt;

                                                                return (
                                                                    <div key={idx} className={styles.referenceItem}>
                                                                        {excerpt && (
                                                                            <p className={styles.referenceQuote}>"{excerpt}"</p>
                                                                        )}
                                                                        <p className={styles.referenceSource}>
                                                                            — {sourceName}
                                                                            {page && `, Page ${page}`}
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })
                    )}
                </section>

                <div className={styles.actionsSection}>
                    <Link to={`/qbank/${bank}/mock-exams`} className={`${styles.actionButton} ${styles.secondaryButton}`}>
                        ← Back to Mock Exams
                    </Link>
                    <Link
                        to={`/qbank/${bank}/mock-exams`}
                        className={`${styles.actionButton} ${styles.primaryButton}`}
                        onClick={(e) => {
                            e.preventDefault();
                            // Navigate and trigger new exam
                            navigate(`/qbank/${bank}/mock-exams`);
                        }}
                    >
                        Take Another Exam
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default MockExamResults;
